import useTranslation from 'next-translate/useTranslation'
import { FC, ReactChild, RefAttributes, RefObject, forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { FlexboxGrid, InputGroup, InputNumber, Loader } from 'rsuite'
import { useRouter } from 'next/router'
import RemindRoundIcon from '@rsuite/icons/RemindRound'
import DoingRoundIcon from '@rsuite/icons/DoingRound'
import debounce from 'lodash/debounce'

import { getSalesUnit } from '../lib/salesUnit'
import ConditionalWrapper from './ConditionalWrapper'
import usePrice from '../services/usePrice'
import { useAuth } from '../services/useAuth'
import useInternalTraffic from './hooks/useInternalTraffic'
import VariantTooltip from './VariantTooltip'
import { getSupplierDeliveryDetails } from '../lib/service'
import { isSuperUser } from '../lib/supplier'
import { DiscountProduct, Product, ProductQuantity } from '../utils/types/Product'
import { getCountryAndLocaleStrings } from '../utils/locales'
import { disableEditOnScroll } from '../utils/event-utils'
import useCyModel from '../services/useCyModel'
import ProductTotalQuantitySection from './ProductTotalQuantitySection'
import MinusIcon from './Icons/MinusIcon'
import PlusIcon from './Icons/PlusIcon'
import { getIsOverExactStock, getStockLimitStatus, getProductManualStockLimit, getTotalProductQuantity, hasStockBackDate, StockLimitStatus } from '../lib/product'
import { isOrderPriceLimitReached } from '../lib/resources/price'
import StopwatchIcon from './Icons/StopwatchIcon'

import styles from '../styles/ProductQuantitySection.module.less'

export interface ProductVariantsRef {
  mainRef: RefObject<HTMLDivElement>
  chooseQuantityRef: RefObject<HTMLDivElement>
}

interface ProductVariantsProps extends RefAttributes<ProductVariantsRef> {
  product: Product
  productQuantity: ProductQuantity[]
  setProductQuantity: React.Dispatch<React.SetStateAction<ProductQuantity[]>>
  isOnCartPage?: boolean
  upcomingDiscount?: DiscountProduct
  currentDiscount?: DiscountProduct
  isMoqWarningActive?: boolean
  takeRateExcludeQuantity?: number
  setIsMoqWarningActive?: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
  hideTotalQuantitySection?: boolean
  updateProductQuantity?: (newProductQuantity: ProductQuantity[]) => Promise<void>
  isUpdatingQuantities?: boolean
}

const ProductVariants: FC<ProductVariantsProps> = forwardRef((props, ref) => {
  const {
    product,
    productQuantity,
    setProductQuantity,
    upcomingDiscount,
    currentDiscount: passedDiscount,
    isMoqWarningActive,
    takeRateExcludeQuantity = 0,
    setIsMoqWarningActive,
    className = '',
    hideTotalQuantitySection = false,
    isOnCartPage = false,
    isUpdatingQuantities,
    updateProductQuantity,
  } = props

  const initialProductQuantityRef = useRef<ProductQuantity[] | null>(null)
  const { t } = useTranslation('products')
  const { isInternalTraffic } = useInternalTraffic()
  const { locale: countryAndLocale } = useRouter()
  const { user } = useAuth()

  const mainRef = useRef<HTMLDivElement>(null)
  const chooseQuantityRef = useRef<HTMLDivElement>(null)

  // Forward both main ref and choose quantity refs
  useImperativeHandle(ref, () => ({
    mainRef,
    chooseQuantityRef,
  }), [mainRef.current, chooseQuantityRef.current])

  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const totalQuantity = getTotalProductQuantity(productQuantity)
  const {
    final: {
      total: price,
    },
    moq,
  } = usePrice(product, totalQuantity, { excludeQuantity: takeRateExcludeQuantity })
  const { deliveryTime: shipmentDays } = getSupplierDeliveryDetails(product.supplier[0], country)
  const productManualStockLimit = getProductManualStockLimit(product)
  const isQuantityButtonDisabled = (prodQuantity: ProductQuantity) => (
    (!product?.product && Number(productQuantity[0].quantity) <= moq)
    || (prodQuantity.exactStockLevel === 0 && prodQuantity.quantity === 0)
    || isUpdatingQuantities
  )

  const getProductSalesUnit = (count: number) => t(getSalesUnit(product), { count })

  const getMinValue = () => (product?.product ? 0 : moq)

  const isManualStockLimitAvailable = (variantStockLimit: number | null) => (
    isInternalTraffic && (Boolean(variantStockLimit) || Boolean(productManualStockLimit))
  )

  // Store initial product quantity to compare with updated quantities
  useEffect(() => {
    if (productQuantity.length && initialProductQuantityRef.current === null) {
      initialProductQuantityRef.current = productQuantity
    }
  }, [productQuantity])

  // Need to wrap this in a useMemo to prevent debouncedUpdateProductQuantity from being recreated
  const debouncedUpdateProductQuantity = useMemo(
    () => debounce((newProductQuantity: ProductQuantity[]) => {
      const updatedQuantities = newProductQuantity.filter((item) => {
        const itemProd = initialProductQuantityRef.current
          ?.find((quantityItem) => (
            quantityItem.variantId === item.variantId
          ))

        return itemProd?.quantity !== item.quantity
      })

      updateProductQuantity?.(updatedQuantities)
      if (updatedQuantities.length) { // Only reset after some update has been made
        initialProductQuantityRef.current = null
      }
    }, 1000),
    [],
  )

  const handleQuantityChange = (id: string, type: 'increment' | 'decrement') => () => {
    // disable only for cart variant section on decrement
    if (type === 'decrement' && (productQuantity.length === 1 && Number(productQuantity[0].quantity) <= moq)) return

    const elementIndex = productQuantity.findIndex((quantityObj) => quantityObj.id === id)
    const element = productQuantity[elementIndex]
    const valueChange = type === 'increment' ? 1 : -1
    const newProductQuantity = productQuantity.map(
      (qty) => {
        if (qty.id === id) {
          const newQuantity = Number(qty.quantity) + valueChange
          // Changing 0 quantities to -1 to allow user to delete multiple variants at a time
          return { ...qty, quantity: updateProductQuantity && newQuantity === 0 ? -1 : newQuantity }
        }
        return qty
      },
    )

    if (type === 'decrement' && element && Number(element.quantity) > 0) {
      setProductQuantity(newProductQuantity)
    } else if (type === 'increment') {
      setProductQuantity(newProductQuantity)
    }

    if (updateProductQuantity) {
      debouncedUpdateProductQuantity(newProductQuantity)
    }

    // Hide highlight alert when user interacts with quantity
    setIsMoqWarningActive?.(false)
  }

  const handleIncrementInput = (id: string) => (val: number | string) => {
    let valAsInt = parseInt(String(val), 10)
    valAsInt = valAsInt < 0 ? 0 : valAsInt
    setProductQuantity((prevProductQuantity) => {
      const newProductQuantity = prevProductQuantity.map(
        (qty) => (qty.id === id ? ({ ...qty, quantity: valAsInt }) : qty),
      )
      if (updateProductQuantity) {
        debouncedUpdateProductQuantity(newProductQuantity)
      }

      return newProductQuantity
    })

    // Hide highlight alert when user interacts with quantity
    setIsMoqWarningActive?.(false)
  }

  useCyModel('ProductQuantity', {
    product,
    productQuantity,
    moq,
    setProductQuantity,
    handleQuantityChange,
  }, false)

  const handleOnBlur = (ele: ProductQuantity) => {
    if (ele.exactStockLevel && Number(ele.quantity) > ele.exactStockLevel) {
      handleIncrementInput(ele.id)(Number(ele.exactStockLevel))
      return
    }

    if (!ele.quantity || Number.isNaN(+ele.quantity)) {
      handleIncrementInput(ele.id)(product?.product ? 0 : moq)
    }
  }

  const renderMinimumQuantitySection = () => {
    if (upcomingDiscount && moq <= Number(totalQuantity)) {
      const remainingQuantity = upcomingDiscount['price.quantity'] - Number(totalQuantity)
      return (
        <span className={styles['add-quantity-for-discount']}>
          {t('Save {{ discountPercent }} % by adding {{ quantity }} {{ salesUnit }}', {
            quantity: remainingQuantity,
            discountPercent: upcomingDiscount.discountPercentage,
            salesUnit: getProductSalesUnit(remainingQuantity),
          })}
        </span>
      )
    }
    return `${t('Minimum')}: ${moq} ${getProductSalesUnit(moq)}`
  }

  const renderTooltipWrapper = (ele: ProductQuantity, condition: boolean, child: ReactChild) => {
    const isOverExactStock = getIsOverExactStock(ele)
    const isOverManualLimit = Number(ele.quantity) > Number(ele.manualStockLimit)

    return (
      <ConditionalWrapper
        p={condition}
        wrapper={(children) => (
          <VariantTooltip
            shipmentDays={shipmentDays}
            variantItem={ele}
            salesUnits={
              ele.manualStockLimit && ele.variantId
                ? getProductSalesUnit(ele.manualStockLimit)
                : getProductSalesUnit(Number(productManualStockLimit))
            }
            productStockLimit={productManualStockLimit}
            showOutOfStockContent={isOverExactStock && !hasStockBackDate(ele.stockDate)}
            showOverManualStockContent={!isOverExactStock && isOverManualLimit}
            showInternalStockLimitContent={
              isManualStockLimitAvailable(ele.manualStockLimit)
            }
          >
            <span>{children}</span>
          </VariantTooltip>
        )}
      >
        {child}
      </ConditionalWrapper>
    )
  }

  const renderVariantLabel = (ele: ProductQuantity) => {
    const getIcon = () => {
      const limitStatus = getStockLimitStatus(ele)

      switch (limitStatus) {
        case StockLimitStatus.HAS_STOCK_BACK_DATE:
          return (
            <FlexboxGrid
              align="middle"
              className={styles['stock-back-date']}
            >
              <DoingRoundIcon />
              <p>{t('Restocking')}</p>
            </FlexboxGrid>
          )

        case StockLimitStatus.EXCEEDS_EXACT_STOCK:
          return (
            <FlexboxGrid
              align="middle"
              className={styles['out-of-stock']}
            >
              <RemindRoundIcon />
              <p>{t('Last of stock')}</p>
            </FlexboxGrid>
          )

        case StockLimitStatus.EXCEEDS_MANUAL_LIMIT:
          return (
            <FlexboxGrid
              align="bottom"
              className={styles['out-of-msl']}
            >
              <StopwatchIcon />
              <p className="margin-left-spacer-half">{t('only a few left')}</p>
            </FlexboxGrid>
          )

        default:
          return null
      }
    }

    const icon = getIcon()

    return (
      <FlexboxGrid
        align="middle"
        justify="space-between"
      >
        <p>{ele.label}</p>
        {icon && renderTooltipWrapper(
          ele,
          true,
          <div className="margin-right-spacer-half">
            {icon}
          </div>,
        )}
      </FlexboxGrid>
    )
  }

  return (
    <div
      className={`${styles['product-quantity']} ${className}`}
      ref={mainRef}
    >
      <div>
        {!isOnCartPage && (
          <FlexboxGrid
            ref={chooseQuantityRef}
            className="margin-bottom-spacer"
            justify="space-between"
            align="middle"
          >
            <b
              data-testid="spp-product-moq"
              className={isMoqWarningActive ? styles['under-moq-highlight'] : ''}
            >
              {renderMinimumQuantitySection()}
            </b>
            {!!product?.product?.length && (
            <b className={isMoqWarningActive ? styles['under-moq-highlight'] : ''}>
              {t('Choose quantity')}
            </b>
            )}
          </FlexboxGrid>
        )}
        {productQuantity.map((ele) => (
          <FlexboxGrid
            data-testid="variant"
            className={`${styles['variant-row']} ${isOnCartPage ? styles['hide-border'] : ''}`}
            justify="space-between"
            align="middle"
            key={ele.id}
          >
            <FlexboxGrid.Item className="flex-grow-1 flex-shrink-1 margin-right-spacer">
              <div
                data-testid="variant-label"
                className={styles['variant-label']}
              >
                {renderVariantLabel(ele)}
              </div>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <InputGroup data-testid={`variant-${ele.label}`}>
                <InputGroup.Button
                  disabled={isQuantityButtonDisabled(ele)}
                  onClick={handleQuantityChange(ele.id, 'decrement')}
                  size="sm"
                >
                  <MinusIcon />
                </InputGroup.Button>
                {renderTooltipWrapper(
                  ele,
                  isManualStockLimitAvailable(ele.manualStockLimit)
                    || hasStockBackDate(ele.stockDate),
                  <InputNumber
                    data-testid={`variant-${ele.label}-input`}
                    className={styles['variant-input-number']}
                    onChange={handleIncrementInput(ele.id)}
                    value={isUpdatingQuantities || ele.quantity === -1 ? 0 : ele.quantity}
                    disabled={ele.exactStockLevel === 0}
                    size="sm"
                    min={getMinValue()}
                    aria-label={`variant-${ele.label}`}
                    onBlur={() => handleOnBlur(ele)}
                    onWheelCapture={disableEditOnScroll}
                  />,
                )}
                {isUpdatingQuantities && <Loader />}
                <InputGroup.Button
                  onClick={handleQuantityChange(ele.id, 'increment')}
                  size="sm"
                  // Disable only for cart variant section if the order limit
                  // was reached and for SPP if exceeds exact stock
                  disabled={
                   (!isSuperUser(user) && isOrderPriceLimitReached(price, product)) || getStockLimitStatus(ele) === 'EXCEEDS_EXACT_STOCK' || isUpdatingQuantities
                  }
                >
                  <PlusIcon />
                </InputGroup.Button>
              </InputGroup>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        ))}
      </div>

      {!hideTotalQuantitySection && !isOnCartPage && (
        <ProductTotalQuantitySection
          product={product}
          productQuantity={productQuantity}
          passedDiscount={passedDiscount}
          takeRateExcludeQuantity={takeRateExcludeQuantity}
          totalQuantity={totalQuantity}
          isMoqWarningActive={isMoqWarningActive}
        />
      )}
    </div>
  )
})

export default ProductVariants
